import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, Video, MapPin, CalendarPlus, Download } from 'lucide-react';

const API_URL = window.location.origin;

const EventCard = ({ event, past }) => {
  const starts = new Date(event.starts_at);
  return (
    <Card className={`card-base ${past ? 'opacity-60' : ''}`} data-testid={`legacy-event-${event.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-heading text-lg font-semibold text-forest">{event.title}</p>
              {past && <Badge className="bg-gray-100 text-gray-500 text-xs">Past</Badge>}
            </div>
            <div className="flex items-center gap-4 mt-1 text-sm text-gray-500 flex-wrap">
              <span className="flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5 text-terracotta" />
                {starts.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5 text-terracotta" />
                {event.time} {event.timezone_label || 'ET'} · {event.duration_minutes} min
              </span>
              {event.location && (
                <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-terracotta" />{event.location}</span>
              )}
            </div>
            {event.description && <p className="text-sm text-gray-600 mt-2 whitespace-pre-line">{event.description}</p>}
          </div>
        </div>
        {!past && (
          <div className="flex items-center gap-2 mt-4 flex-wrap">
            {event.zoom_link && (
              <Button size="sm" className="bg-forest hover:bg-forest/90 text-white" onClick={() => window.open(event.zoom_link, '_blank')} data-testid={`legacy-event-join-${event.id}`}>
                <Video className="h-4 w-4 mr-2" /> Join Zoom
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => window.open(event.google_url, '_blank')} data-testid={`legacy-event-google-${event.id}`}>
              <CalendarPlus className="h-4 w-4 mr-2" /> Google Calendar
            </Button>
            <Button size="sm" variant="outline" onClick={() => window.open(`${API_URL}/api/legacy/events/${event.id}/ics`, '_blank')} data-testid={`legacy-event-ics-${event.id}`}>
              <Download className="h-4 w-4 mr-2" /> Apple / Outlook (.ics)
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const LegacyCalendar = () => {
  const [events, setEvents] = useState({ upcoming: [], past: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/legacy/events`)
      .then(res => setEvents(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="h-32 bg-sand rounded-xl animate-pulse" />;

  return (
    <div className="space-y-6" data-testid="legacy-calendar">
      <Card className="card-base border-terracotta/20 bg-gradient-to-br from-terracotta/5 to-transparent">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-1">
            <Calendar className="h-5 w-5 text-terracotta" />
            <p className="text-xs uppercase tracking-widest text-terracotta">Legacy Calendar</p>
          </div>
          <p className="font-heading text-xl text-forest">Gatherings, Witness Circles, and moments that shape your year.</p>
          <p className="text-sm text-gray-500 mt-1">You'll be reminded a week before, the day before, and an hour before each event.</p>
        </CardContent>
      </Card>

      {events.upcoming.length > 0 ? (
        <div className="space-y-3">
          {events.upcoming.map(e => <EventCard key={e.id} event={e} />)}
        </div>
      ) : (
        <p className="text-center text-sm text-gray-400 py-6" data-testid="legacy-calendar-empty">No upcoming events yet — new gatherings will appear here.</p>
      )}

      {events.past.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs uppercase tracking-widest text-gray-400">Recently Held</p>
          {events.past.map(e => <EventCard key={e.id} event={e} past />)}
        </div>
      )}
    </div>
  );
};

export default LegacyCalendar;
